import { DietType, type Traits, type Vec2, type Obstacle } from './types'
import type { Food } from './food'

let nextId = 0

export const MAX_ENERGY = 150
const BASE_ENERGY = 80

/** 乱数ヘルパー */
function rand(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function randDiet(): DietType {
  const r = Math.random()
  if (r < 0.60) return DietType.Herbivore  // 草食: 60%（増加）
  if (r < 0.85) return DietType.Carnivore  // 肉食: 25%
  return DietType.Omnivore                  // 雑食: 15%（削減）
}

export function randomTraits(): Traits {
  const diet = randDiet()
  const speedBase = diet === DietType.Carnivore ? 2.5 : diet === DietType.Herbivore ? 1.5 : 2.0
  return {
    speed: rand(speedBase * 0.7, speedBase * 1.3),
    visionRange: rand(40, 120),
    size: rand(
      diet === DietType.Carnivore ? 5 : 3,
      diet === DietType.Carnivore ? 9 : 6,
    ),
    diet,
    hue: diet === DietType.Herbivore ? rand(90, 150) : diet === DietType.Carnivore ? rand(0, 30) : rand(200, 280),
    metabolismRate: rand(0.8, 1.2),
    /** 繁殖力: 高いほど繁殖に必要なエネルギーが低い（雑食は繁殖しにくく調整） */
    fertilityRate: diet === DietType.Omnivore ? rand(0.6, 0.9) : rand(0.8, 1.2),
  }
}

export class Organism {
  id: number
  pos: Vec2
  vel: Vec2
  energy: number
  age: number
  alive: boolean
  traits: Traits
  /** 世代番号（0が初期個体） */
  generation: number
  /** 親個体のID（初期個体はnull） */
  parentId: number | null

  private wanderAngle: number
  private stateTimer: number
  /** 獲物が見つからなかった連続フレーム数 */
  private starvingTicks: number

  constructor(
    x: number,
    y: number,
    traits?: Traits,
    generation = 0,
    parentId: number | null = null,
  ) {
    this.id = nextId++
    this.pos = { x, y }
    this.vel = { x: 0, y: 0 }
    this.energy = BASE_ENERGY
    this.age = 0
    this.alive = true
    this.traits = traits ?? randomTraits()
    this.wanderAngle = Math.random() * Math.PI * 2
    this.stateTimer = 0
    this.generation = generation
    this.parentId = parentId
    this.starvingTicks = 0
  }

  update(
    dt: number,
    foods: Food[],
    organisms: Organism[],
    worldW: number,
    worldH: number,
    obstacles: Obstacle[],
  ): void {
    this.age += dt

    // 基礎代謝（食性ごとに差を付ける）
    // 雑食は食料源が多い分、代謝コストを高めに設定（1.1→1.35）
    const dietMultiplier = this.traits.diet === DietType.Carnivore ? 1.6
      : this.traits.diet === DietType.Omnivore ? 1.35 : 0.85
    const drain = 0.008 * this.traits.metabolismRate * dietMultiplier
      * (1 + this.traits.speed * 0.3 + this.traits.size * 0.1)
    this.energy -= drain * dt * 60

    // 肉食・雑食が獲物なしでさまよう場合は追加エネルギー消費（飢餓状態）
    if (this.traits.diet === DietType.Carnivore) {
      this.starvingTicks++
      if (this.starvingTicks > 120) {
        this.energy -= 0.04 * dt * 60  // 飢餓ペナルティ
      }
    } else if (this.traits.diet === DietType.Omnivore) {
      this.starvingTicks++
      if (this.starvingTicks > 180) {
        this.energy -= 0.02 * dt * 60  // 雑食にも飢餓ペナルティ（肉食より軽め）
      }
    }

    if (this.energy <= 0) {
      this.alive = false
      return
    }

    // 最も近い食料を探す
    const target = this.findTarget(foods, organisms)

    if (target) {
      this.starvingTicks = 0
      this.moveToward(target, dt)
    } else {
      this.wander(dt)
    }

    // 障害物との衝突処理
    this.resolveObstacles(obstacles)

    // 境界折り返し
    this.pos.x = ((this.pos.x % worldW) + worldW) % worldW
    this.pos.y = ((this.pos.y % worldH) + worldH) % worldH

    // 食事判定
    this.eat(foods, organisms)

    // エネルギー上限
    if (this.energy > MAX_ENERGY) this.energy = MAX_ENERGY

    this.stateTimer += dt
  }

  private findTarget(foods: Food[], organisms: Organism[]): Vec2 | null {
    const { visionRange, diet } = this.traits
    let best: Vec2 | null = null
    let bestDist = visionRange

    if (diet === DietType.Herbivore || diet === DietType.Omnivore) {
      for (const f of foods) {
        if (!f.alive) continue
        const d = dist(this.pos, f.pos)
        if (d < bestDist) {
          bestDist = d
          best = f.pos
        }
      }
    }

    if (diet === DietType.Carnivore || diet === DietType.Omnivore) {
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        // 草食か自分より十分小さい個体を狙う（雑食は捕食条件を厳しく）
        const isValidPrey = o.traits.diet === DietType.Herbivore
          || (diet === DietType.Omnivore && o.traits.size < this.traits.size * 0.6)
        if (!isValidPrey) continue
        const d = dist(this.pos, o.pos)
        if (d < bestDist) {
          bestDist = d
          best = o.pos
        }
      }
    }

    return best
  }

  private moveToward(target: Vec2, dt: number): void {
    const dx = target.x - this.pos.x
    const dy = target.y - this.pos.y
    const len = Math.sqrt(dx * dx + dy * dy) || 1
    const spd = this.traits.speed * 60
    this.vel.x = (dx / len) * spd
    this.vel.y = (dy / len) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private wander(dt: number): void {
    this.wanderAngle += (Math.random() - 0.5) * 0.3
    const spd = this.traits.speed * 40
    this.vel.x = Math.cos(this.wanderAngle) * spd
    this.vel.y = Math.sin(this.wanderAngle) * spd
    this.pos.x += this.vel.x * dt
    this.pos.y += this.vel.y * dt
  }

  private resolveObstacles(obstacles: Obstacle[]): void {
    for (const obs of obstacles) {
      const dx = this.pos.x - obs.pos.x
      const dy = this.pos.y - obs.pos.y
      const d = Math.sqrt(dx * dx + dy * dy)
      const minDist = obs.radius + this.traits.size
      if (d < minDist && d > 0) {
        const nx = dx / d
        const ny = dy / d
        this.pos.x = obs.pos.x + nx * minDist
        this.pos.y = obs.pos.y + ny * minDist
        const dot = this.vel.x * nx + this.vel.y * ny
        this.vel.x -= 2 * dot * nx
        this.vel.y -= 2 * dot * ny
      }
    }
  }

  private eat(foods: Food[], organisms: Organism[]): void {
    const { diet, size } = this.traits

    if (diet === DietType.Herbivore || diet === DietType.Omnivore) {
      for (const f of foods) {
        if (!f.alive) continue
        if (dist(this.pos, f.pos) < size + f.radius) {
          this.energy += f.energy
          this.starvingTicks = 0
          f.alive = false
        }
      }
    }

    if (diet === DietType.Carnivore || diet === DietType.Omnivore) {
      for (const o of organisms) {
        if (!o.alive || o.id === this.id) continue
        // 肉食は草食のみ捕食、雑食はサイズ差が大きい場合のみ捕食
        const isValidPrey = diet === DietType.Carnivore
          ? o.traits.diet === DietType.Herbivore
          : o.traits.size < size * 0.6
        if (!isValidPrey) continue
        if (dist(this.pos, o.pos) < size + o.traits.size) {
          this.energy += o.energy * 0.5
          this.starvingTicks = 0
          o.alive = false
        }
      }
    }
  }
}

function dist(a: Vec2, b: Vec2): number {
  const dx = a.x - b.x
  const dy = a.y - b.y
  return Math.sqrt(dx * dx + dy * dy)
}
